import { useMemo } from 'react';
import { useEnrichedEvents } from '../../ses/context/EnrichedEventsContext';
import { PlayerShiftSummary } from '../types/PlayerShiftTypes';
import { usePlayerShifts } from './usePlayerShifts';
import { isPlayerInvolvedInEvent, isShiftRelevantEvent } from '../utils/shiftUtils';

/**
 * Returns a single player's shift summary with each shift's events array populated.
 *
 * This is separated from usePlayerShifts to avoid the expensive event-to-shift
 * correlation for all players — only the selected player pays this cost.
 */
export function usePlayerShiftDetail(playerId: string | null): PlayerShiftSummary | null {
  const shiftsMap = usePlayerShifts();
  const events = useEnrichedEvents();

  return useMemo(() => {
    if (!playerId) return null;


    const summary = shiftsMap.get(playerId);
    if (!summary) {
      return null;
    }

    // Filter non-shift events that involve this player
    const playerEvents = events.filter(
      (e) => isShiftRelevantEvent(e) && isPlayerInvolvedInEvent(e, playerId)
    );

    // For each shift, find events that fall within the shift's time window
    const enrichedShifts = summary.shifts.map((shift) => {
      if (!shift.isCompleted) return { ...shift, events: [] };

      const shiftEvents = playerEvents.filter((e) => {
        const eventTimeMs = e.timestampMs ?? 0;
        return eventTimeMs >= shift.startTimeMs && eventTimeMs <= shift.endTimeMs;
      });

      // Sort events by timestamp within the shift
      shiftEvents.sort((a, b) => (a.timestampMs ?? 0) - (b.timestampMs ?? 0));

      return { ...shift, events: shiftEvents };
    });

    return {
      ...summary,
      shifts: enrichedShifts,
    };
  }, [playerId, shiftsMap, events]);
}
